// src/components/telemetry/TrajectoryExplorer.jsx
// Traiettorie di un allenamento salvato, nel dettaglio: mappa satellitare con
// zoom (rotella, pinch, pulsanti), giro analizzato colorato per velocità, giro
// di confronto e giro istruttore sovrapposti, migliori giri degli altri piloti
// a richiesta; per ogni curva il punto di frenata e il punto più lento; al
// passaggio del mouse velocità e distacco nello stesso punto della pista.
// Usa solo le traiettorie (replay leggero): nessun dato in più da scaricare.
// Auto con più piloti: lapNums = i giri del pilota scelto ("Tutti i giri"),
// compareOptions = migliori giri degli altri piloti [{ key, name, color, lap }].
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Pane, Polyline, Polygon, CircleMarker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { colorFromName } from '../../utils/colors';
import { formatLap } from './TelemetryCharts';
import './TrajectoryExplorer.css';

const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const R = 6371000;
const toRad = (d) => d * Math.PI / 180;
const COMPARE_COLOR = '#ffffff';
// Livelli fissi: ogni gruppo ha il suo piano, così l'ordine di disegno non
// dipende da quando un elemento viene (ri)aggiunto alla mappa (per esempio
// l'asfalto che torna passando da Satellite a Pista resta sotto le traiettorie)
const PANES = { track: 410, ghost: 420, others: 425, ref: 430, focus: 440, marks: 450 };
const INSTRUCTOR_COLOR = '#12bfff';

function meters(a, b) {
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}

// Scala colore velocità (lento → veloce), come nel report PDF
function speedColor(t) {
  const stops = [[0, [214, 39, 40]], [0.5, [245, 196, 0]], [1, [32, 170, 80]]];
  const k = Math.max(0, Math.min(1, t));
  const [a, b] = k <= 0.5 ? [stops[0], stops[1]] : [stops[1], stops[2]];
  const f = (k - a[0]) / (b[0] - a[0]);
  const c = a[1].map((x, i) => Math.round(x + (b[1][i] - x) * f));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

/**
 * Giro preparato per la mappa: punti validi, distanza e tempo progressivi
 * (tempo integrato dalla velocità e riscalato sul tempo del giro), indice
 * del punto più vicino della linea centrale (per le curve).
 */
function prepareLap(lap, center) {
  const pts = (Array.isArray(lap?.trail) ? lap.trail : []).filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lon));
  if (pts.length < 10) return null;
  const n = pts.length;
  const dist = new Float64Array(n), time = new Float64Array(n), cidx = new Int32Array(n);
  for (let i = 1; i < n; i++) {
    const ds = meters(pts[i - 1], pts[i]);
    dist[i] = dist[i - 1] + ds;
    const v = Math.max(1, ((Number(pts[i].speed) || 0) + (Number(pts[i - 1].speed) || 0)) / 2 / 3.6);
    time[i] = time[i - 1] + ds / v;
  }
  const k = lap.lapTime && time[n - 1] > 0 ? lap.lapTime / time[n - 1] : 1;
  for (let i = 0; i < n; i++) time[i] *= k;
  // punto della linea centrale più vicino, con ricerca in avanti (niente salti fra tratti vicini)
  if (center?.length) {
    const m = center.length;
    let cursor = -1;
    for (let i = 0; i < n; i++) {
      let best = -1, bd = Infinity;
      const scan = (from, count) => {
        for (let q = 0; q < count; q++) {
          const j = ((from + q) % m + m) % m;
          const dx = (center[j].lon - pts[i].lon) * Math.cos(toRad(pts[i].lat)), dy = center[j].lat - pts[i].lat;
          const d = dx * dx + dy * dy;
          if (d < bd) { bd = d; best = j; }
        }
      };
      if (cursor < 0) scan(0, m); else { scan(cursor - 10, 80); if (Math.sqrt(bd) * 111320 > 40) scan(0, m); }
      cursor = best; cidx[i] = best;
    }
  }
  const sp = pts.map(p => Number(p.speed) || 0).sort((a, b) => a - b);
  return { lap, pts, dist, time, cidx, lo: sp[Math.floor(n * 0.03)], hi: sp[Math.floor(n * 0.97)] };
}

/** Per ogni curva: punto più lento e inizio frenata (velocità massima nei 200 m prima). */
function cornerMarks(pl, corners) {
  if (!pl || !corners.length) return [];
  const out = [];
  const inCorner = (j, c) => (c.endIdx >= c.startIdx ? j >= c.startIdx && j <= c.endIdx : j >= c.startIdx || j <= c.endIdx);
  corners.forEach(c => {
    let first = -1, apex = -1;
    for (let i = 0; i < pl.pts.length; i++) {
      if (!inCorner(pl.cidx[i], c)) { if (first >= 0) break; continue; }
      if (first < 0) first = i;
      if (apex < 0 || pl.pts[i].speed < pl.pts[apex].speed) apex = i;
    }
    if (apex < 0) return;
    let brake = -1;
    if (pl.dist[first] > 60) {
      for (let i = first; i >= 0 && pl.dist[first] - pl.dist[i] <= 200; i--) if (brake < 0 || pl.pts[i].speed > pl.pts[brake].speed) brake = i;
      for (let i = first; i <= apex; i++) if (pl.pts[i].speed > pl.pts[brake].speed) brake = i;
      if (pl.pts[brake].speed - pl.pts[apex].speed < 8) brake = -1;   // nessuna vera staccata
    }
    out.push({ c, apex, brake });
  });
  return out;
}

/** Linea colorata per velocità: tratti consecutivi dello stesso colore uniti in una sola linea. */
function speedRuns(pl) {
  const runs = [];
  const B = 14;
  let cur = null;
  for (let i = 1; i < pl.pts.length; i++) {
    const t = (pl.pts[i].speed - pl.lo) / ((pl.hi - pl.lo) || 1);
    const b = Math.max(0, Math.min(B, Math.round(t * B)));
    if (!cur || cur.b !== b) {
      cur = { b, color: speedColor(b / B), pos: [[pl.pts[i - 1].lat, pl.pts[i - 1].lon]] };
      runs.push(cur);
    }
    cur.pos.push([pl.pts[i].lat, pl.pts[i].lon]);
  }
  return runs;
}

const latlngs = (pts) => pts.map(p => [p.lat, p.lon]);
const ll = (arr) => (Array.isArray(arr) ? arr.filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lon)).map(p => [p.lat, p.lon]) : []);

function nearest(pl, lat, lon) {
  let best = -1, bd = Infinity;
  const c = Math.cos(toRad(lat));
  for (let i = 0; i < pl.pts.length; i++) {
    const dx = (pl.pts[i].lon - lon) * c, dy = pl.pts[i].lat - lat;
    const d = dx * dx + dy * dy;
    if (d < bd) { bd = d; best = i; }
  }
  return { i: best, m: Math.sqrt(bd) * 111320 };
}

/* ─────────── comandi della mappa ─────────── */

function FitTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target?.bounds) return;
    map.fitBounds(target.bounds, { padding: [28, 28], maxZoom: target.maxZoom || 20, animate: target.animate !== false });
  }, [map, target]);
  return null;
}

/**
 * Lettura al passaggio del mouse (o al tocco): stato proprio, così muovere il
 * mouse non ridisegna le traiettorie. Il riquadro va dentro la mappa (portal).
 */
function HoverLayer({ focus, refLap, refLabel, refColor }) {
  const map = useMap();
  const [hover, setHover] = useState(null);
  const raf = useRef(0);
  useEffect(() => { setHover(null); }, [focus, refLap]);
  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  const probe = (ll) => {
    if (!focus) return;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const a = nearest(focus, ll.lat, ll.lng);
      if (a.i < 0 || a.m > 35) { setHover(null); return; }
      const p = focus.pts[a.i];
      let r = null;
      if (refLap) {
        const b = nearest(refLap, p.lat, p.lon);
        if (b.i >= 0 && b.m <= 25) r = { i: b.i, speed: refLap.pts[b.i].speed, dt: focus.time[a.i] - refLap.time[b.i] };
      }
      setHover({ lat: p.lat, lon: p.lon, speed: p.speed, dist: focus.dist[a.i], t: focus.time[a.i], r });
    });
  };
  useMapEvents({ mousemove: (e) => probe(e.latlng), click: (e) => probe(e.latlng), mouseout: () => setHover(null) });
  const sgn = (x) => (x > 0 ? '+' : x < 0 ? '−' : '');
  const readout = (
    <div className="tx-readout" aria-live="polite">
      {hover && focus ? (
        <>
          <div><span>Giro {focus.lap.lapNum}</span><b>{Math.round(hover.speed)} km/h</b><em>{Math.round(hover.dist)} m · {hover.t.toFixed(2)} s</em></div>
          {hover.r && (
            <div><span>{refLabel}</span><b>{Math.round(hover.r.speed)} km/h</b>
              <em className={hover.r.dt > 0.005 ? 'is-lose' : hover.r.dt < -0.005 ? 'is-gain' : ''}>{sgn(hover.r.dt)}{Math.abs(hover.r.dt).toFixed(2)} s · {sgn(hover.speed - hover.r.speed)}{Math.abs(Math.round(hover.speed - hover.r.speed))} km/h</em></div>
          )}
        </>
      ) : <div className="tx-readout__hint">Passa sulla traiettoria per leggere velocità e distacco</div>}
    </div>
  );
  return (
    <>
      {hover && <CircleMarker center={[hover.lat, hover.lon]} radius={7} pathOptions={{ color: '#ffffff', weight: 2.5, fillColor: '#0d0f14', fillOpacity: 0.6 }} interactive={false} />}
      {hover?.r && <CircleMarker center={[refLap.pts[hover.r.i].lat, refLap.pts[hover.r.i].lon]} radius={5} pathOptions={{ color: refColor, weight: 2.5, fillOpacity: 0 }} interactive={false} />}
      {createPortal(readout, map.getContainer())}
    </>
  );
}

// colore di un token del tema (le linee su canvas non leggono le variabili CSS)
const themeColor = (name, fallback) => {
  try { return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback; } catch { return fallback; }
};

/* ─────────── componente ─────────── */

export default function TrajectoryExplorer({ circuit, drivers = [], pilotMac, focusLapNum, compareLapNum, instructorLapNum, height = 560, lapNums = null, compareOptions = null, resetKey = null }) {
  const center = useMemo(() => (Array.isArray(circuit?.sectors) ? circuit.sectors.filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lon)) : []), [circuit]);
  const corners = useMemo(() => (Array.isArray(circuit?.corners) ? circuit.corners : [])
    .filter(c => Number.isInteger(c?.startIdx) && Number.isInteger(c?.endIdx) && center.length)
    .sort((a, b) => a.startIdx - b.startIdx)
    .map((c, i) => ({ ...c, num: i + 1, label: c.name || `Curva ${i + 1}` })), [circuit, center.length]);

  const pilot = drivers.find(d => d.mac === pilotMac) || null;
  const laps = useMemo(() => (pilot?.lapHistory || []).filter(l => !l.partial && l.lapTime > 0), [pilot]);
  // "Tutti i giri": solo quelli del pilota scelto, se indicati
  const ownSet = useMemo(() => (Array.isArray(lapNums) ? new Set(lapNums.map(Number)) : null), [lapNums]);

  // vista "Pista" (disegno del circuito, stesse coordinate del GPS: precisa al
  // metro) di default; il satellite a forte zoom può essere spostato di qualche metro
  const [satellite, setSatellite] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showMarks, setShowMarks] = useState(true);
  const [others, setOthers] = useState([]);           // mac di altri piloti (miglior giro)
  const [fit, setFit] = useState(null);
  const [cornerSel, setCornerSel] = useState(null);

  useEffect(() => { setOthers([]); }, [pilotMac, resetKey]);

  const focus = useMemo(() => prepareLap(laps.find(l => l.lapNum === focusLapNum), center), [laps, focusLapNum, center]);
  const compare = useMemo(() => (compareLapNum != null && compareLapNum !== focusLapNum && compareLapNum !== instructorLapNum
    ? prepareLap(laps.find(l => l.lapNum === compareLapNum), center) : null), [laps, compareLapNum, focusLapNum, instructorLapNum, center]);
  const instructor = useMemo(() => (instructorLapNum != null && instructorLapNum !== focusLapNum
    ? prepareLap(laps.find(l => Number(l.lapNum) === Number(instructorLapNum)), center) : null), [laps, instructorLapNum, focusLapNum, center]);
  const ref = instructor || compare;                  // riferimento per il distacco al passaggio del mouse

  const otherDrivers = useMemo(() => (Array.isArray(compareOptions)
    ? compareOptions.map(o => ({ mac: o.key, name: o.name, color: o.color, lap: o.lap }))
    : drivers.filter(d => d.mac !== pilotMac).map(d => {
      const valid = (d.lapHistory || []).filter(l => !l.partial && l.lapTime > 0 && Number(l.lapNum) !== Number(d.instructorLapNum));
      const best = valid.reduce((b, l) => (!b || l.lapTime < b.lapTime ? l : b), null);
      return best ? { mac: d.mac, name: d.fullName || d.tag || d.mac, color: colorFromName(d.fullName || d.tag || d.mac), lap: best } : null;
    }).filter(Boolean)), [drivers, pilotMac, compareOptions]);
  const otherPrepared = useMemo(() => otherDrivers.filter(o => others.includes(o.mac)).map(o => ({ ...o, pl: prepareLap(o.lap, center) })).filter(o => o.pl), [otherDrivers, others, center]);

  const focusRuns = useMemo(() => (focus ? speedRuns(focus) : []), [focus]);
  const marks = useMemo(() => ({
    focus: cornerMarks(focus, corners),
    ref: cornerMarks(ref, corners),
  }), [focus, ref, corners]);

  // inquadratura iniziale: tutto il circuito
  const allBounds = useMemo(() => {
    const src = center.length ? center : (focus?.pts || []);
    return src.length ? latlngs(src) : null;
  }, [center, focus]);
  useEffect(() => { if (allBounds) setFit({ bounds: allBounds, animate: false, maxZoom: 18 }); setCornerSel(null); }, [allBounds]);

  const zoomCorner = useCallback((c) => {
    if (!c) { setCornerSel(null); if (allBounds) setFit({ bounds: allBounds, maxZoom: 18 }); return; }
    const m = center.length;
    const idx = [];
    const from = c.startIdx - Math.round(m * 0.025), to = (c.endIdx >= c.startIdx ? c.endIdx : c.endIdx + m) + Math.round(m * 0.01);
    for (let j = from; j <= to; j++) idx.push(((j % m) + m) % m);
    setCornerSel(c.num);
    setFit({ bounds: idx.map(j => [center[j].lat, center[j].lon]), maxZoom: 19.5 });
  }, [center, allBounds]);

  // posizioni delle linee calcolate una volta (non a ogni ridisegno)
  const shape = useMemo(() => {
    const lb = ll(circuit?.leftBorder), rb = ll(circuit?.rightBorder);
    return {
      borders: lb.length > 10 && rb.length > 10 ? [lb, rb] : null,
      kerbs: (Array.isArray(circuit?.kerbs) ? circuit.kerbs : []).map(k => ll(k?.points)).filter(k => k.length > 1),
    };
  }, [circuit]);
  const pos = useMemo(() => ({
    center: center.length > 2 ? [...latlngs(center), [center[0].lat, center[0].lon]] : null,
    all: laps.filter(l => ![focusLapNum, compareLapNum, instructorLapNum].includes(l.lapNum) && (!ownSet || ownSet.has(Number(l.lapNum)))).map(l => ({ n: l.lapNum, p: latlngs(l.trail || []) })),
    compare: compare ? latlngs(compare.pts) : null,
    instructor: instructor ? latlngs(instructor.pts) : null,
    others: otherPrepared.map(o => ({ mac: o.mac, color: o.color, p: latlngs(o.pl.pts) })),
  }), [center, laps, focusLapNum, compareLapNum, instructorLapNum, compare, instructor, otherPrepared, ownSet]);

  if (!center.length && !focus) return <div className="tx-empty">Traiettorie non disponibili per questa sessione.</div>;

  const startCenter = center[0] || focus?.pts[0];
  const refName = instructor ? 'Istruttore' : compare ? `Giro ${compare.lap.lapNum}` : null;
  const trackColor = themeColor('--rs-canvas-track', '#50545a');
  const edgeColor = themeColor('--rs-text-3', '#9aa1ae');
  const compareColor = COMPARE_COLOR;     // l'asfalto è scuro in entrambi i temi

  return (
    <div className="tx">
      <div className={`tx-map ${satellite ? 'is-sat' : 'is-plain'}`} style={{ height }}>
        <MapContainer center={[startCenter.lat, startCenter.lon]} zoom={16} preferCanvas zoomSnap={0.25} zoomDelta={0.5} wheelPxPerZoomLevel={90}
          maxZoom={21} minZoom={12} attributionControl={false} style={{ height: '100%', width: '100%' }}>
          {satellite && <TileLayer url={ESRI} maxNativeZoom={19} maxZoom={21} />}
          <FitTo target={fit} />

          <Pane name="tx-track" style={{ zIndex: PANES.track }}>
          {!satellite && shape.borders && (
            <>
              <Polygon positions={shape.borders} pathOptions={{ stroke: false, fillColor: trackColor, fillOpacity: 1, fillRule: 'evenodd' }} interactive={false} />
              <Polyline positions={shape.borders[0]} pathOptions={{ color: edgeColor, weight: 1.2, opacity: 0.9 }} interactive={false} />
              <Polyline positions={shape.borders[1]} pathOptions={{ color: edgeColor, weight: 1.2, opacity: 0.9 }} interactive={false} />
            </>
          )}
          {!satellite && shape.kerbs.map((k, i) => (
            <React.Fragment key={`k${i}`}>
              <Polyline positions={k} pathOptions={{ color: '#d92d20', weight: 4, opacity: 1, lineCap: 'butt' }} interactive={false} />
              <Polyline positions={k} pathOptions={{ color: '#ffffff', weight: 4, opacity: 1, dashArray: '4 4', lineCap: 'butt' }} interactive={false} />
            </React.Fragment>
          ))}
          {pos.center && (satellite || !shape.borders) && (
            <Polyline key={`trk${satellite}`} positions={pos.center} pathOptions={{ color: satellite ? '#ffffff' : trackColor, weight: satellite ? 2 : 14, opacity: satellite ? 0.35 : 1, lineCap: 'round', lineJoin: 'round' }} interactive={false} />
          )}
          </Pane>
          <Pane name="tx-ghost" style={{ zIndex: PANES.ghost }}>
          {showAll && pos.all.map(l => (
            <Polyline key={`all${l.n}`} positions={l.p} pathOptions={{ color: '#9aa1ae', weight: 1.5, opacity: 0.45 }} interactive={false} />
          ))}
          </Pane>
          <Pane name="tx-others" style={{ zIndex: PANES.others }}>
          {pos.others.map(o => (
            <Polyline key={`o${o.mac}`} positions={o.p} pathOptions={{ color: o.color, weight: 3, opacity: 0.9 }} interactive={false} />
          ))}
          </Pane>
          <Pane name="tx-ref" style={{ zIndex: PANES.ref }}>
          {pos.compare && (
            <>
              <Polyline positions={pos.compare} pathOptions={{ color: '#0d0f14', weight: 5.5, opacity: 0.85 }} interactive={false} />
              <Polyline positions={pos.compare} pathOptions={{ color: compareColor, weight: 2.6, opacity: 1 }} interactive={false} />
            </>
          )}
          {pos.instructor && <Polyline positions={pos.instructor} pathOptions={{ color: INSTRUCTOR_COLOR, weight: 3.5, opacity: 0.95 }} interactive={false} />}
          </Pane>
          <Pane name="tx-focus" style={{ zIndex: PANES.focus }}>
          {focusRuns.map((r, i) => (
            <Polyline key={`f${i}`} positions={r.pos} pathOptions={{ color: r.color, weight: 4.5, opacity: 1, lineCap: 'round' }} interactive={false} />
          ))}
          </Pane>

          <Pane name="tx-marks" style={{ zIndex: PANES.marks }}>
          {showMarks && [['ref', ref, refName ? 'rif.' : ''], ['focus', focus, 'tu']].map(([k, pl, who]) => pl && marks[k].map(mk => (
            <React.Fragment key={`${k}${mk.c.num}`}>
              {mk.brake >= 0 && (
                <CircleMarker center={[pl.pts[mk.brake].lat, pl.pts[mk.brake].lon]} radius={k === 'focus' ? 5 : 4}
                  pathOptions={{ color: '#0d0f14', weight: 1.5, fillColor: k === 'focus' ? '#ff3b30' : INSTRUCTOR_COLOR, fillOpacity: 1 }}>
                  <Tooltip direction="top" offset={[0, -4]}>{mk.c.label} · inizio frenata ({who}) a {Math.round(pl.pts[mk.brake].speed)} km/h</Tooltip>
                </CircleMarker>
              )}
              <CircleMarker center={[pl.pts[mk.apex].lat, pl.pts[mk.apex].lon]} radius={k === 'focus' ? 4.5 : 3.5}
                pathOptions={{ color: k === 'focus' ? '#0d0f14' : INSTRUCTOR_COLOR, weight: 2, fillColor: '#ffffff', fillOpacity: 1 }}>
                <Tooltip direction="top" offset={[0, -4]}>{mk.c.label} · punto più lento ({who}) {Math.round(pl.pts[mk.apex].speed)} km/h</Tooltip>
              </CircleMarker>
            </React.Fragment>
          )))}

          <HoverLayer focus={focus} refLap={ref} refLabel={refName} refColor={instructor ? INSTRUCTOR_COLOR : compareColor} />
          </Pane>
        </MapContainer>

        <div className="tx-tools">
          <button type="button" className={!satellite ? 'is-on' : ''} onClick={() => setSatellite(false)} title="Disegno del circuito: preciso al metro">Pista</button>
          <button type="button" className={satellite ? 'is-on' : ''} onClick={() => setSatellite(true)} title="Foto satellitare (a forte zoom può essere spostata di qualche metro)">Satellite</button>
          <button type="button" className={showMarks ? 'is-on' : ''} onClick={() => setShowMarks(v => !v)} title="Inizio frenata e punto più lento in ogni curva">Frenate</button>
          <button type="button" className={showAll ? 'is-on' : ''} onClick={() => setShowAll(v => !v)} title="Tutti i giri del pilota">Tutti i giri</button>
        </div>
        {satellite && <div className="tx-credit">© Esri, Maxar, Earthstar Geographics</div>}
      </div>

      <div className="tx-bar">
        <div className="tx-corners" role="group" aria-label="Zoom sulle curve">
          <button type="button" className={cornerSel == null ? 'is-on' : ''} onClick={() => zoomCorner(null)}>Tutto</button>
          {corners.map(c => (
            <button type="button" key={c.num} className={cornerSel === c.num ? 'is-on' : ''} onClick={() => zoomCorner(c)} title={c.label}>{c.num}</button>
          ))}
        </div>
        {otherDrivers.length > 0 && (
          <div className="tx-others" role="group" aria-label="Migliori giri degli altri piloti">
            <span>Confronta con</span>
            {otherDrivers.map(o => (
              <button type="button" key={o.mac} className={others.includes(o.mac) ? 'is-on' : ''} style={{ '--c': o.color }}
                onClick={() => setOthers(v => (v.includes(o.mac) ? v.filter(x => x !== o.mac) : [...v, o.mac]))}>
                <i />{o.name} · {formatLap(o.lap.lapTime)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="tx-legend">
        {focus && <span><i className="tx-lg tx-lg--speed" />Giro {focus.lap.lapNum} · {formatLap(focus.lap.lapTime)} (colore = velocità)</span>}
        {instructor && <span><i className="tx-lg" style={{ background: INSTRUCTOR_COLOR }} />Istruttore · giro {instructor.lap.lapNum} · {formatLap(instructor.lap.lapTime)}</span>}
        {compare && <span><i className="tx-lg tx-lg--cmp" />Confronto · giro {compare.lap.lapNum} · {formatLap(compare.lap.lapTime)}</span>}
        {showMarks && <span><i className="tx-dot tx-dot--brake" />inizio frenata</span>}
        {showMarks && <span><i className="tx-dot tx-dot--apex" />punto più lento</span>}
        <span className="tx-legend__hint">rotella o pinch: zoom · trascina: sposta · numeri: zoom sulla curva</span>
      </div>
    </div>
  );
}
